import { makeAutoObservable, runInAction } from "mobx";
import { isAxiosError } from "axios";
import http from "../../services/http";
import { IFeatureMeta, IPaymentSubscription } from "../companyFeaturesStore";

export interface IAdminCompanyFeature {
  id: string;
  feature: IFeatureMeta | null;
  quantity: number | null;
  expiresAt: string | null;
  createdAt: string;
  subscription?: IPaymentSubscription | null;
}

export interface SaveClientFeature {
  featureCode?: string;
  quantity: number;
  expiresAt: string;
}

export function featureErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data;
    if (data?.errors) {
      return Object.values(data.errors).flat().join(" ");
    }
    if (error.response?.status === 403) return "Управление доступно только суперадминистратору.";
    if (error.response?.status === 404) return "Клиент, компания или выданная фича не найдены.";
    if (typeof data?.message === "string") return data.message;
  }
  return "Не удалось выполнить запрос. Попробуйте ещё раз.";
}

export default class AdminClientFeaturesStore {
  data: IAdminCompanyFeature[] = [];
  availableFeatures: IFeatureMeta[] = [];
  isLoading = true;
  isSaving = false;
  error?: string;

  constructor(private readonly ownerId: string) {
    makeAutoObservable(this);
  }

  private get url() {
    return `/owners/${this.ownerId}/features`;
  }

  async load() {
    runInAction(() => { this.isLoading = true; this.error = undefined; });
    try {
      const result = await http.get(this.url);
      runInAction(() => {
        this.data = result.data.data;
        this.availableFeatures = result.data.availableFeatures;
      });
    } catch (error) {
      runInAction(() => { this.error = featureErrorMessage(error); });
    } finally {
      runInAction(() => { this.isLoading = false; });
    }
  }

  async save(values: SaveClientFeature, id?: string) {
    runInAction(() => { this.isSaving = true; });
    try {
      const result = id
        ? await http.put(`${this.url}/${id}`, values)
        : await http.post(this.url, values);
      runInAction(() => {
        const record: IAdminCompanyFeature = result.data.data;
        this.data = id ? this.data.map(item => item.id === id ? record : item) : [record, ...this.data];
      });
    } finally {
      runInAction(() => { this.isSaving = false; });
    }
  }

  async revoke(id: string) {
    runInAction(() => { this.isSaving = true; });
    try {
      const result = await http.post(`${this.url}/${id}/revoke`);
      runInAction(() => {
        this.data = this.data.map(item => item.id === id ? result.data.data : item);
      });
    } finally {
      runInAction(() => { this.isSaving = false; });
    }
  }
}
